import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

function ManagerSelector({ managers, value, onChange, label }) {
  return (
    <div className="lor-h2h-selector">
      <label className="lor-h2h-selector-label">{label}</label>
      <select
        className="lor-h2h-select"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">Select manager…</option>
        {managers.map(m => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
    </div>
  );
}

function StatBox({ label, value, sub, highlight }) {
  return (
    <div className={`lor-h2h-stat${highlight ? ' lor-h2h-stat--highlight' : ''}`}>
      <div className="lor-h2h-stat-val">{value}</div>
      <div className="lor-h2h-stat-label">{label}</div>
      {sub && <div className="lor-h2h-stat-sub">{sub}</div>}
    </div>
  );
}

export default function LORH2H({ api }) {
  const [managerList, setManagerList] = useState([]);
  const [managerA, setManagerA] = useState('');
  const [managerB, setManagerB] = useState('');
  const [h2h, setH2H]           = useState(null);
  const [loading, setLoading]   = useState(false);
  const [listLoading, setListLoading] = useState(true);

  // Load manager names from standings / records
  useEffect(() => {
    fetch(`${api}/api/lor/standings`)
      .then(r => r.json())
      .then(data => {
        const names = (data || []).map(s => s.display_name).filter(Boolean);
        setManagerList(names);
        setListLoading(false);
      })
      .catch(() => setListLoading(false));
  }, [api]);

  useEffect(() => {
    if (!managerA || !managerB || managerA === managerB) return;
    setLoading(true);
    setH2H(null);
    fetch(`${api}/api/lor/h2h/${encodeURIComponent(managerA)}/${encodeURIComponent(managerB)}`)
      .then(r => r.json())
      .then(data => { setH2H(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api, managerA, managerB]);

  const totals = h2h?.totals || {};
  const aStats = totals[managerA] || { wins: 0, losses: 0, pf: 0, pa: 0 };
  const bStats = totals[managerB] || { wins: 0, losses: 0, pf: 0, pa: 0 };
  const streak = h2h?.h2h_streak || {};
  const last5  = h2h?.last5 || [];
  const biggestWin  = h2h?.biggest_win;
  const closestGame = h2h?.closest_game;
  const playoffGames = h2h?.playoff_games || [];
  const playoffTotals = h2h?.playoff_totals || {};
  const aPlayoff = playoffTotals[managerA] || { wins: 0, losses: 0 };
  const bPlayoff = playoffTotals[managerB] || { wins: 0, losses: 0 };
  const legacyGames = h2h?.historical_regular_games || 0;
  const legacyPlayoffGames = h2h?.historical_playoff_games || 0;

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="⚔️" size={18} /> Head-to-Head Archive</h2>
        <div className="lor-page-meta">All-time matchup history</div>
      </div>

      <div className="lor-h2h-pickers">
        {listLoading ? (
          <div className="lor-loading">Loading managers…</div>
        ) : (
          <>
            <ManagerSelector
              managers={managerList}
              value={managerA}
              onChange={setManagerA}
              label="Manager A"
            />
            <div className="lor-h2h-vs">vs</div>
            <ManagerSelector
              managers={managerList}
              value={managerB}
              onChange={setManagerB}
              label="Manager B"
            />
          </>
        )}
      </div>

      {loading && <div className="lor-loading">Loading head-to-head…</div>}

      {h2h && !loading && h2h.total_games === 0 && (
        <div className="lor-empty">No matchups found between these managers.</div>
      )}

      {h2h && !loading && h2h.total_games > 0 && (
        <div className="lor-h2h-results">
          {/* Record header */}
          <div className="lor-h2h-record-banner lor-card">
            <div className="lor-h2h-record-side">
              <div className="lor-h2h-record-name">{managerA}</div>
              <div className="lor-h2h-record-wl">
                <span className="lor-wins" style={{ fontSize: '2rem' }}>{aStats.wins}</span>
                <span className="lor-h2h-record-label">wins</span>
              </div>
            </div>
            <div className="lor-h2h-record-center">
              <div className="lor-h2h-total-games">{h2h.total_games} games</div>
              {streak.count > 1 && (
                <div className="lor-h2h-streak-badge">
                  {streak.manager} Sleeper-era streak W{streak.count}
                </div>
              )}
            </div>
            <div className="lor-h2h-record-side lor-h2h-record-side--right">
              <div className="lor-h2h-record-name">{managerB}</div>
              <div className="lor-h2h-record-wl">
                <span className="lor-wins" style={{ fontSize: '2rem' }}>{bStats.wins}</span>
                <span className="lor-h2h-record-label">wins</span>
              </div>
            </div>
          </div>

          {/* Stat grid */}
          <div className="lor-h2h-stat-grid">
            <StatBox
              label="Sleeper-era PF"
              value={aStats.pf.toFixed(1)}
              sub={managerA}
              highlight={aStats.pf > bStats.pf}
            />
            <StatBox
              label="Win %"
              value={`${(aStats.wins / (aStats.wins + aStats.losses || 1) * 100).toFixed(0)}%`}
              sub={managerA}
              highlight={aStats.wins > bStats.wins}
            />
            <StatBox
              label="Win %"
              value={`${(bStats.wins / (bStats.wins + bStats.losses || 1) * 100).toFixed(0)}%`}
              sub={managerB}
              highlight={bStats.wins > aStats.wins}
            />
            <StatBox
              label="Sleeper-era PF"
              value={bStats.pf.toFixed(1)}
              sub={managerB}
              highlight={bStats.pf > aStats.pf}
            />
          </div>

          {legacyGames > 0 && (
            <div className="lor-card" style={{ marginBottom: 14 }}>
              <div className="lor-history-section-label">Record coverage</div>
              <div className="lor-h2h-notable-val">Complete all-time W–L record</div>
              <div className="lor-h2h-notable-meta">
                {legacyGames} pre-Sleeper matchup{legacyGames !== 1 ? 's' : ''} added from the commissioner archive. Points, recent form and game-level milestones use verified Sleeper-era scores only.
              </div>
            </div>
          )}

          {/* Notable games */}
          <div className="lor-h2h-notable">
            {biggestWin && (
              <div className="lor-card lor-h2h-notable-card">
                <div className="lor-history-section-label">Biggest Sleeper-era Margin</div>
                <div className="lor-h2h-notable-val">
                  {biggestWin.winner} · {biggestWin.score}
                </div>
                <div className="lor-h2h-notable-meta">
                  +{biggestWin.margin} pts · {biggestWin.season} Week {biggestWin.week}
                </div>
              </div>
            )}
            {closestGame && (
              <div className="lor-card lor-h2h-notable-card">
                <div className="lor-history-section-label">Closest Sleeper-era Game</div>
                <div className="lor-h2h-notable-val">{closestGame.score}</div>
                <div className="lor-h2h-notable-meta">
                  Margin: {closestGame.margin} pts · {closestGame.season} Week {closestGame.week}
                </div>
              </div>
            )}
          </div>

          {/* Playoff meetings */}
          {(h2h.playoff_meetings || 0) > 0 && (
            <div className="lor-card">
              <div className="lor-history-section-label">Playoff Meetings</div>
              <div className="lor-h2h-notable-val" style={{ marginBottom: 8 }}>
                {managerA} {aPlayoff.wins}–{aPlayoff.losses} · {managerB} {bPlayoff.wins}–{bPlayoff.losses}
              </div>
              {legacyPlayoffGames > 0 && (
                <div className="lor-h2h-notable-meta" style={{ marginBottom: 10 }}>
                  Includes {legacyPlayoffGames} aggregate pre-Sleeper playoff meeting{legacyPlayoffGames !== 1 ? 's' : ''}; detailed scores below are Sleeper-era only.
                </div>
              )}
              {playoffGames.map((g, i) => (
                <div key={i} className="lor-h2h-playoff-game">
                  <span className="lor-h2h-playoff-year">{g.season}</span>
                  <span className="lor-history-playoff-winner"><Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {g.winner}</span>
                  <span className="lor-history-playoff-score">{g.w_pts} – {g.l_pts}</span>
                  <span className="lor-history-playoff-loser">{g.loser}</span>
                </div>
              ))}
            </div>
          )}

          {/* Last 5 matchups */}
          {last5.length > 0 && (
            <div className="lor-card">
              <div className="lor-history-section-label">Last {last5.length} Sleeper Matchups</div>
              {[...last5].reverse().map((g, i) => (
                <div key={i} className="lor-h2h-last5-game">
                  <span className="lor-h2h-last5-meta">{g.season} Wk {g.week}</span>
                  <span className="lor-h2h-last5-score">{g.score}</span>
                  <span className="lor-h2h-last5-winner">W: {g.winner}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!managerA || !managerB ? (
        <div className="lor-h2h-prompt lor-empty">
          Select two managers to see their all-time head-to-head history.
        </div>
      ) : null}
    </div>
  );
}
